import { PRODUCTS } from '../../data/products';
import { isoWeekStart, type IsoDate } from '../../domain/dates';
import { toCents, toEuros } from '../../domain/money';
import type { PlannedDay, PlanWarning } from '../../domain/plan';
import { dinnerAlternatives, portionCents } from '../../domain/planner/alternatives';
import { periodBudget } from '../../domain/planner/budget';
import { createCatalog } from '../../domain/price-book';
import { isFishDinner, isQuick, type Recipe } from '../../domain/recipe';
import type { Household } from '../../domain/household';
import type { Product } from '../../domain/types';
import { formatDate, formatMoney, productList } from '../i18n/format';
import { MESSAGES, type Language } from '../i18n/messages';
import { recipeById } from '../recipes/recipe-lookup';
import type { AppData, StoredPlan, StoredTrip } from '../state/app-data';
import { buildRequest, currentPlan } from '../state/plan-service';

/**
 * DE: Eine Zeile der Planliste: Abendessen eines Tages mit seinen Merkmalen.
 * EN: One row of the plan list: a day's dinner with its markers.
 */
export interface DayRow {
  date: IsoDate;
  recipeId: string;
  totalMinutes: number;
  fish: boolean;
  quick: boolean;
  lunchbox: boolean;
  locked: boolean;
  past: boolean;
}

export interface WeekGroup {
  start: IsoDate;
  cost: number;
  days: DayRow[];
}

export interface PlanView {
  budget: { planned: number; limit: number };
  weeks: WeekGroup[];
  warnings: PlanWarning[];
}

const sumTrips = (trips: readonly StoredTrip[]) =>
  toEuros(trips.reduce((sum, trip) => sum + toCents(trip.total), 0));

function dayRow(
  day: PlannedDay,
  household: Household,
  catalog: ReadonlyMap<string, Product>,
  today: IsoDate,
): DayRow {
  const recipe = recipeById(day.dinner.recipeId);
  return {
    date: day.date,
    recipeId: recipe.id,
    totalMinutes: recipe.totalMinutes,
    fish: isFishDinner(recipe, catalog),
    quick: isQuick(recipe, household.quick),
    lunchbox: recipe.lunchboxFriendly,
    locked: day.dinner.locked,
    past: day.date < today,
  };
}

/**
 * DE: Plan nach ISO-Wochen mit den Einkaufssummen je Woche und dem Budgetvergleich.
 * EN: Plan by ISO week with the trip totals per week and the budget comparison.
 */
export function planView(stored: StoredPlan, data: AppData, today: IsoDate): PlanView {
  const catalog = createCatalog(PRODUCTS);
  const { start } = stored.period;
  // DE: Ein Einkauf vor dem ersten Tag kauft für diesen Plan ein und zählt zur ersten Woche.
  // EN: A trip before the first day shops for this plan and counts in the first week.
  const weekOf = (date: IsoDate) => isoWeekStart(date < start ? start : date);
  const rows = stored.plan.days.map((day) => dayRow(day, data.household, catalog, today));
  const weekStarts = [...new Set(rows.map((row) => weekOf(row.date)))];
  return {
    budget: { planned: sumTrips(stored.trips), limit: periodBudget(data.household, stored.period) },
    weeks: weekStarts.map((weekStart) => ({
      start: weekStart,
      cost: sumTrips(stored.trips.filter((trip) => weekOf(trip.date) === weekStart)),
      days: rows.filter((row) => weekOf(row.date) === weekStart),
    })),
    warnings: stored.plan.warnings,
  };
}

/**
 * DE: Ersatzgerichte für einen Tag des laufenden Plans ab heute, mit Preis pro Portion.
 * EN: Replacement dinners for a day of the running plan from today on, with the price per portion.
 */
export function replaceChoices(
  data: AppData,
  today: IsoDate,
  date: IsoDate,
): { recipe: Recipe; portion: number }[] {
  const stored = currentPlan(data, today);
  if (!stored || date < today) return [];
  if (!stored.plan.days.some((day) => day.date === date)) return [];
  const request = buildRequest(data, stored);
  return dinnerAlternatives(stored.plan, date, request).map((recipe) => ({
    recipe,
    portion: toEuros(Math.round(portionCents(recipe, date, request))),
  }));
}

/**
 * DE: Planerhinweis als Satz; bei gelockerten Regeln steht der Tag getrennt davor.
 * EN: Planner warning as a sentence; for relaxed rules the day stands apart in front.
 */
export function warningText(
  warning: PlanWarning,
  language: Language,
): { day?: string; text: string } {
  const t = MESSAGES[language];
  if (warning.code === 'over-budget') {
    return { text: t.overBudget(formatMoney(warning.amount ?? 0, language)) };
  }
  const text =
    warning.code === 'shops-closed'
      ? t.checkAtHome(productList(warning.productIds ?? [], language))
      : t.relaxedWarnings[warning.code];
  return warning.date
    ? { day: formatDate(warning.date, language, 'weekday-long'), text }
    : { text };
}
